import moment from 'moment';

class TimeEntryInputController {

  constructor($scope, $rootScope, AuthenticationService, TimesheetService, LeaveService, toastr) {
    "ngInject";
    this.timesheetService = TimesheetService;
    this.authenticationService = AuthenticationService;
    this.leaveService = LeaveService;
    this.timeEntry = {};
    this.previousValue = undefined;
    this.toastr = toastr;
  }

  canEditTimeEntry() {
    let beforeStartDate = moment(this.timeEntry.date).isBefore(moment(this.authenticationService.user.startDate));
    let afterCurrentDate = moment(this.timeEntry.date).isAfter(window.Utilities.today()) && this.timeEntry.chargeCode.billable;

    return !(beforeStartDate ||
      this.timesheetService.isTimesheetLocked() ||
      afterCurrentDate ||
      !this._isActiveChargeCode() ||
      this._isNonEditableLeaveType());
  }

  timeEntered() {
    let timeEntry = this.timeEntry;
    const previousValue = this.previousValue;

    timeEntry.hours = this._roundTimeEntry(timeEntry);

    let nonLeaveDailyTotal = this.timesheetService.getNonLeaveDailyTotal(timeEntry.date);

    if(nonLeaveDailyTotal > 24) {
      this.toastr.error("You can’t log more than 24 hours in a day—even superheroes need sleep.","Overachiever Alert!");
      timeEntry.hours = previousValue;
      return;
    }

    let nonAutoBalanceLeaveDailyTotal = this.timesheetService.getNonAutoBalanceLeaveDailyTotal(timeEntry.date);

    if(nonLeaveDailyTotal >= window.Utilities.WORKDAY_HOURS() && nonAutoBalanceLeaveDailyTotal > 0) {
      this.toastr.error("You have added more working hours than your companies required working hours setting ("+window.Utilities.WORKDAY_HOURS()+" hours per day) and you have leave hours recorded.  Please adjust the leave hours prior to adding additional working time.","Overachiever Alert!");
      timeEntry.hours = previousValue;
      return;
    }

    if(!timeEntry.chargeCode.leaveType && timeEntry.hours < 0) {
      this.toastr.error("Your task hours can’t be negative—this isn’t an accounting loophole.", "Reality Check!");
      timeEntry.hours = previousValue;
      return;
    }

    if(timeEntry.chargeCode.leaveType && timeEntry.hours < 0 && nonLeaveDailyTotal < 8) {
      this.toastr.error(" Negative leave only unlocks after you clock "+window.Utilities.WORKDAY_HOURS()+" hours of actual work. Come back when you’ve earned it.", "Dream on, Time Traveler!");
      timeEntry.hours = previousValue;
      return;
    }

    if(timeEntry.chargeCode.leaveType && (nonLeaveDailyTotal + timeEntry.hours) > window.Utilities.WORKDAY_HOURS()) {
      this.toastr.error("Congrats on hitting your quota—tacking on more than "+(window.Utilities.WORKDAY_HOURS() - nonLeaveDailyTotal)+" leave hours now actually cripples the hours you’ve earned.", "Self-Sabotage Alert!");
      timeEntry.hours = previousValue;
      return;
    }

    timeEntry.mode = this.timesheetService.getMode(timeEntry);

    if(this.authenticationService.user.trackLeave) {
      this.leaveService.autoBalanceLeave(timeEntry);
    }

    this.previousValue = timeEntry.hours;
    this.timesheetService.dirtyCheck();
  }

  // Store the previous value when input gains focus
  onInputFocus() {
    this.previousValue = this.timeEntry.hours;
  }

  isNumber(event) {
    window.Utilities.validateNumericInput(event);
  }

  isCurrentDay(dateString) {
    return moment(dateString).isSame(moment().startOf('day'));
  }

  formattedHours(value) {
    if (arguments.length) {
      // Setter: store the raw value
      this.timeEntry.hours = value;
      return value;
    } else {
      // Getter: return formatted value for display
      if (this.timeEntry.hours === null || this.timeEntry.hours === undefined) {
        return '';
      }

      return Math.round(this.timeEntry.hours * 100) / 100;
    }
  }

  _isActiveChargeCode() {
    if (!this.timeEntry.chargeCode.effectiveDate || !this.timeEntry.chargeCode.expirationDate) { return true;}

    // Check if the charge code is active for the specific time entry date, not today's date
    const entryDate = moment(this.timeEntry.date).startOf('day');
    const effectiveDate = moment(this.timeEntry.chargeCode.effectiveDate).startOf('day');
    const expirationDate = moment(this.timeEntry.chargeCode.expirationDate).startOf('day');
    
    // Charge code is active if entry date is on or after effective date and on or before expiration date
    return entryDate.isSameOrAfter(effectiveDate) && entryDate.isSameOrBefore(expirationDate);
  }

  _isNonEditableLeaveType() {
    return this.timeEntry.chargeCode.id === window.Utilities.AUTO_BALANCE_CHARGE_CODE_ID();
  }

  _roundTimeEntry(timeEntry) {
    const roundingInterval = timeEntry.chargeCode.timeEntryRoundingInterval;

    if (!roundingInterval)  return timeEntry.hours

    if(timeEntry.hours === null || timeEntry.hours === undefined) {
      return undefined
    } else {
      // Normalize the input to avoid floating point precision issues
      // Use a high precision multiplier to normalize, then round
      const normalized = Math.round(timeEntry.hours * 1000000) / 1000000;
      
      // Calculate multiplier for rounding interval
      const multiplier = 1 / roundingInterval;
      
      // Check if the value is already a valid multiple (within a small tolerance)
      const remainder = normalized % roundingInterval;
      const tolerance = 0.000001; // Very small tolerance for floating point comparison
      
      // If already a valid multiple (within tolerance), return the normalized value
      if (Math.abs(remainder) < tolerance || Math.abs(remainder - roundingInterval) < tolerance) {
        return normalized;
      }
      
      // Otherwise, round to the nearest multiple
      const result = Math.round(normalized * multiplier) / multiplier;
      
      // Round to appropriate decimal places to clean up floating point artifacts
      const decimalPlaces = Math.max(0, -Math.log10(roundingInterval));
      return Math.round(result * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
    }
  }
}

export default TimeEntryInputController;



// WEBPACK FOOTER //
// ./client/pages/timesheet/components/time-entry-input/time-entry-input.controller.js